import {
  CheckoutAPI,
  IExtraDetail,
  IGetPriceEstimateRequest,
  IGetPriceEstimateResponse,
  NibblesDiscount,
} from "@api-clients/checkout";
import { TNibblesProduct, TSprinklesProduct } from "@api-clients/subscriptions";
import { throwIfFalsy } from "@/autovalidate";
import { SubscriptionItemType } from "@/primary-models";
import dayjs from "dayjs";

import {
  getNestedFieldValue,
  normaliseApiDiscountToStoreDiscount,
  removeNestedField,
  resolveEffectiveDiscountCode,
  transformCatDataForApi,
  updateNestedField,
} from "../utils";
import {
  createCapiCustomDataFromFreshOptions,
  transformCats,
} from "../transformer";

import {
  defaultDelivery,
  defaultSubscription,
  defaultUser,
  type TCatFieldName,
  type TCatFormDataInput,
  type TDeliveryData,
  type TSubscriptionData,
  type TUserFormData,
} from "@./suf-store";
import type { IBaseStoreState } from "./baseStore.types";

const CHECKOUT_API_BASE_URL = throwIfFalsy(
  process.env["NEXT_PUBLIC_CHECKOUT_URL"],
  "NEXT_PUBLIC_CHECKOUT_URL",
);
const checkoutApi = new CheckoutAPI(CHECKOUT_API_BASE_URL);

/**
 * Shared price estimate logic. Accepts store accessors so it can be
 * composed into any Zustand store that holds cats/delivery/subscription.
 */
export function buildCallGetPriceEstimate(
  get: () => { subscription: TSubscriptionData; cats: Record<string, TCatFormDataInput>; delivery: TDeliveryData },
  set: (
    partial: Partial<{
      delivery: TDeliveryData;
      subscription: TSubscriptionData;
      cats: Record<string, TCatFormDataInput> }>) => void,
): (requestedProps: Partial<IGetPriceEstimateRequest>) => Promise<IGetPriceEstimateResponse> {
  return async (requestedProps) => {
    const { subscription, cats, delivery } = get();
    const effectiveDiscountCode = resolveEffectiveDiscountCode(requestedProps.discountCode, subscription);

    const baseRequestObj: IGetPriceEstimateRequest = {
      deliveryDate: dayjs(delivery.deliveryDate).format("YYYY-MM-DD"),
      discountCode: effectiveDiscountCode,
      discountNibbles: subscription.nibblesUpsell?.added ? NibblesDiscount.FIFTY_OFF : undefined,
      cats: transformCatDataForApi(cats, subscription),
    };

    const mergedRequestObj = {
      ...baseRequestObj,
      ...requestedProps,
      discountCode: effectiveDiscountCode,
    };

    const response = await checkoutApi.getPriceEstimate(mergedRequestObj);
    if (response) {
      const { deliveryDate, discount, cats: catsPriceEstimate, totals } = response;
      const latestSubscription = get().subscription;
      const responseDiscount = normaliseApiDiscountToStoreDiscount(discount, latestSubscription);

      // we currently only support one box of nibbles per checkout
      // cross-sell/add a new cat supports multiple boxes of nibbles so we'll need to update this logic
      // to account for quantity and type of nibbles
      const nibblesUpsell: IExtraDetail | undefined = catsPriceEstimate.flatMap((cat) => cat.trial.extras)
        .find((extra) => extra.productTitle === SubscriptionItemType.NIBBLES_CHICKEN
      || extra.productTitle === SubscriptionItemType.NIBBLES_SALMON);

      const sprinklesExtras = catsPriceEstimate
        .flatMap((cat) => cat.trial.extras)
        .filter((extra) => extra.productTitle === SubscriptionItemType.SPRINKLES);

      const totalSprinklesQuantity = sprinklesExtras
        .reduce((total, extra) => total + extra.quantity, 0);

      const sprinklesUpsell: IExtraDetail | undefined = sprinklesExtras[0]
        ? { ...sprinklesExtras[0], quantity: totalSprinklesQuantity }
        : undefined;

      // Create capiCustomData for tracking
      const capiCustomData = createCapiCustomDataFromFreshOptions(
        response,
        latestSubscription.productType || "FRESH_TRIAL",
      );

      set({
        delivery: {
          ...get().delivery,
          deliveryDate,
        },
        subscription: {
          ...latestSubscription,
          trial: {
            planType: catsPriceEstimate[0].trial.planType,
            totalOriginalPrice: Number.parseFloat(totals.trial.original.overall.total),
            totalDiscountedPrice: Number.parseFloat(totals.trial.discounted.overall.total),
            originalPricePerDay: Number.parseFloat(totals.trial.original.mealPlan.perCatPerDay),
            discountedPricePerDay: Number.parseFloat(totals.trial.discounted.mealPlan.perCatPerDay),
            originalPricePerPortion: Number.parseFloat(totals.trial.original.mealPlan.perPortion),
            discountedPricePerPortion: Number.parseFloat(totals.trial.discounted.mealPlan.perPortion),
          },
          ongoing: {
            planType: catsPriceEstimate[0].ongoing.planType,
            totalOriginalPrice: Number.parseFloat(totals.ongoing.original.overall.total),
            totalDiscountedPrice: Number.parseFloat(totals.ongoing.discounted.overall.total),
            originalPricePerDay: Number.parseFloat(totals.ongoing.original.mealPlan.perCatPerDay),
            discountedPricePerDay: Number.parseFloat(totals.ongoing.discounted.mealPlan.perCatPerDay),
            originalPricePerPortion: Number.parseFloat(totals.ongoing.original.mealPlan.perPortion),
            discountedPricePerPortion: Number.parseFloat(totals.ongoing.discounted.mealPlan.perPortion),
          },
          ...(responseDiscount && { discount: responseDiscount }),
          nibblesUpsell: nibblesUpsell
            ? {
              added: nibblesUpsell.quantity > 0,
              discountedPrice: Number.parseFloat(nibblesUpsell.discountedPrice),
              originalPrice: Number.parseFloat(nibblesUpsell.totalPrice),
              type: nibblesUpsell.productTitle as TNibblesProduct,
              quantity: nibblesUpsell.quantity,
            }
            : latestSubscription.nibblesUpsell,
          sprinklesUpsell: sprinklesUpsell ? {
            added: sprinklesUpsell.quantity > 0,
            discountedPrice: Number.parseFloat(sprinklesUpsell.discountedPrice),
            originalPrice: Number.parseFloat(sprinklesUpsell.unitPrice),
            type: sprinklesUpsell.productTitle as TSprinklesProduct,
            quantity: sprinklesUpsell.quantity,
          } : defaultSubscription.sprinklesUpsell,
          capiCustomData,
        },
        cats: transformCats(cats, catsPriceEstimate),
      });
    }
    return response;
  };
}

export function buildBaseStoreSlice(
  set: (partial: Partial<IBaseStoreState> | ((state: IBaseStoreState) => Partial<IBaseStoreState>)) => void,
  get: () => IBaseStoreState,
) {
  return {
    /* --- Initialisation ---*/
    isStoreInitialised: false,
    setIsStoreInitialised: (value: boolean) => set({ isStoreInitialised: value }),

    /* --- Version Mismatch Flag ---*/
    versionMismatchDetected: false,
    setVersionMismatchDetected: (value: boolean) => set({ versionMismatchDetected: value }),

    /* --- Cats ---*/
    cats: {},

    activeCatId: "",

    activeCat: () => get().cats[get().activeCatId],

    updateCat: (id: string, cat: Partial<TCatFormDataInput>) => set((state) => ({
      cats: {
        ...state.cats,
        [id]: { ...state.cats[id], ...cat },
      },
      savedAt: dayjs().format(),
    })),

    getCat: (id: string) => get().cats[id],

    getActiveCatIds: () => Object.keys(get().cats).filter((catId) => !get().cats[catId].softDelete),

    softDeleteCat: (id: string) => set((state) => ({
      cats: {
        ...state.cats,
        [id]: { ...state.cats[id], softDelete: true },
      },
    })),

    restoreCat: (id: string) => set((state) => ({
      cats: {
        ...state.cats,
        [id]: { ...state.cats[id], softDelete: false },
      },
    })),

    migrateCatData: (fieldNames: TCatFieldName[], sourceId: string, targetIds: string[]) => set((state) => {
      const source = state.cats[sourceId];
      if (!source) { return {}; }

      const updatedCats = { ...state.cats };

      const updateTargetCat = (targetId: string) => {
        if (!updatedCats[targetId]) { return; }

        let target = { ...updatedCats[targetId] };

        for (const fieldName of fieldNames) {
          const sourceValue = getNestedFieldValue(source, fieldName);
          if (sourceValue !== undefined) {
            target = updateNestedField(target, fieldName, sourceValue);
          }
        }

        updatedCats[targetId] = target;
      };

      for (const targetId of targetIds) {
        updateTargetCat(targetId);
      }

      return {
        cats: updatedCats,
      };
    }),

    revertMigrateCatData: (fieldNames: TCatFieldName[], targetIds: string[]) => set((state) => {
      const updatedCats = { ...state.cats };

      const removeFromTargetCat = (targetId: string) => {
        if (!updatedCats[targetId]) { return; }

        let target = { ...updatedCats[targetId] };

        for (const fieldName of fieldNames) {
          target = removeNestedField(target, fieldName);
        }

        updatedCats[targetId] = target;
      };

      for (const targetId of targetIds) {
        removeFromTargetCat(targetId);
      }

      return { cats: updatedCats };
    }),

    setActiveCatId: (id: string) => set({ activeCatId: id }),

    /* --- User ---*/
    user: defaultUser,
    updateUser: (data: Partial<TUserFormData>) => set((state) => ({
      user: {
        ...state.user,
        ...data,
      },
      savedAt: dayjs().format(),
    })),

    /* --- Delivery ---*/
    delivery: defaultDelivery,
    updateDelivery: (data: Partial<TDeliveryData>) => set((state) => ({
      delivery: {
        ...state.delivery,
        ...data,
      },
      savedAt: dayjs().format(),
    })),

    /* --- Subscription ---*/
    subscription: defaultSubscription,
    updateSubscription: (data: Partial<TSubscriptionData>) => set((state) => ({
      subscription: {
        ...state.subscription,
        ...data,
      },
      savedAt: dayjs().format(),
    })),

    /* --- Call getPriceEstimate and update store ---*/
    callGetPriceEstimateAndUpdateStore: buildCallGetPriceEstimate(get, set),

    /* --- Loading state ---*/
    isLoading: false,
    setIsLoading: (state: boolean) => set({ isLoading: state }),
  };
}

